/**
 * Glass Theme Factory
 *
 * Creates a complete glassmorphic Theme object from a minimal color palette.
 * Eliminates duplication across light/dark and student/educator theme files
 * by providing sensible mode-specific defaults for structural properties
 * (card, nav, modal, dropdown, shadows, borders) while requiring only the
 * unique visual identity (colors, gradients, orbs) as input.
 *
 * Usage:
 *   createGlassTheme({ meta, background, accent, colors, coverFallback, pageBackgrounds })
 */

import {
  Theme,
  ThemeCategory,
  ThemeMode,
  BackgroundTheme,
  AccentTheme,
  CoverFallbackTheme,
  PageBackgroundTheme,
  PageId,
} from './theme-types';

/**
 * Color palette for mode-specific structural defaults.
 * These control the "chrome" around the unique visual identity:
 * cards, modals, dropdowns, borders, text, shadows.
 */
export interface GlassThemeColors {
  text: {
    primary: string;
    secondary: string;
    tertiary: string;
    muted: string;
    inverse: string;
  };
  /** Base color for shadow rgba — dark themes use black, light use slate */
  shadowBase: string;
  /** Shadow opacity scale [sm, md, lg, xl] */
  shadowOpacity: [number, number, number, number];
  /** Accent shadow color (rgba string including alpha) */
  accentShadow: string;
  /** Border base rgba color — opacity varies per weight */
  borderBase: string;
  /** Border opacity scale [light, medium, strong] */
  borderOpacity: [number, number, number];
  card: {
    background: string;
    backgroundHover: string;
    border: string;
    shadow: string;
  };
  nav: {
    background: string;
    border: string;
    shadow: string;
    activeBackground: string;
    activeText: string;
  };
  modal: {
    background: string;
    border: string;
    shadow: string;
    backdropColor: string;
  };
  dropdown: {
    background: string;
    backgroundHover: string;
    border: string;
    shadow: string;
  };
}

/**
 * Input for createGlassTheme — only the parts that are truly unique per theme.
 */
export interface GlassThemeConfig {
  meta: {
    id: string;
    name: string;
    description: string;
    category: ThemeCategory;
    mode: ThemeMode;
    previewColors: string[];
  };
  background: BackgroundTheme;
  accent: AccentTheme;
  colors: GlassThemeColors;
  coverFallback: CoverFallbackTheme;
  pageBackgrounds?: Partial<Record<PageId, PageBackgroundTheme>>;
}

/**
 * Create a complete Theme object from a minimal config.
 * Structural properties (backdrop filter, border radius/width, backdrop blur)
 * use shared defaults since they're identical across all themes.
 */
export function createGlassTheme(config: GlassThemeConfig): Theme {
  const { meta, background, accent, colors, coverFallback, pageBackgrounds } = config;

  return {
    ...meta,
    background,
    text: colors.text,
    accent,

    shadow: {
      sm: `0 2px 4px ${colors.shadowBase}, ${colors.shadowOpacity[0]})`,
      md: `0 4px 8px ${colors.shadowBase}, ${colors.shadowOpacity[1]})`,
      lg: `0 8px 16px ${colors.shadowBase}, ${colors.shadowOpacity[2]})`,
      xl: `0 12px 24px ${colors.shadowBase}, ${colors.shadowOpacity[3]})`,
      accent: colors.accentShadow,
    },

    border: {
      light: `${colors.borderBase}, ${colors.borderOpacity[0]})`,
      medium: `${colors.borderBase}, ${colors.borderOpacity[1]})`,
      strong: `${colors.borderBase}, ${colors.borderOpacity[2]})`,
      radius: '10px',
      width: '1px',
    },

    card: {
      ...colors.card,
      backdropFilter: 'blur(20px) saturate(180%)',
    },

    nav: {
      ...colors.nav,
      backdropFilter: 'blur(20px) saturate(180%)',
    },

    modal: {
      ...colors.modal,
      backdropBlur: '2px',
    },

    dropdown: colors.dropdown,

    coverFallback,
    pageBackgrounds,
  };
}
